import React, { FC } from 'react';

import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { statFormatter } from '@nhl/formatter-utilities';

import { LeaderItem, Measure } from '~/core/types';
import { commonMessages } from '~/utility/commonMessages';

import LeaderSpotlight from '../leaderSpotlight';

import {
    LeaderContent,
    LeaderListContainer,
    LeaderListItem,
    LeaderListName,
    LeaderListRank,
    LeaderListValue,
    LeadersNavLink,
    NoDataMessage,
} from './styles';

// This allows 0 to show for GAA (Not --). The other leader stats wil display as intended too
const ALLOW_ZERO_IN_STATS = true;

type LeaderListProps = {
    activeCategory: string;
    activeLeaderIndex: number;
    activeMeasure: Measure;
    setActiveLeaderIndex: (value: number) => void;
    seasonId?: number;
};

const scrollToTop = () => {
    window.scrollTo(0, 0);
};

const getLeaderValue = (leader: LeaderItem, measure: string, category: string): number | string => {
    const categoryMap = {
        gaa: 'goalsAgainstAverage',
        savePctg: 'savePercentage',
    };

    return categoryMap[category]
        ? statFormatter.displayValueAs(categoryMap[category])(leader[measure], ALLOW_ZERO_IN_STATS)
        : leader[measure];
};

const genLeaderList = (
    activeMeasure: Measure,
    activeCategory: string,
    activeLeaderIndex: number,
    setActiveLeaderIndex: (value: number) => void,
    intl: IntlShape,
): Array<JSX.Element> => {
    const { leaders, measure } = activeMeasure;

    const rankMap = {};
    leaders.forEach((leader, index) => {
        // When ranking players we use the unformatted values so the data is not truncated
        const measureValue = leader[measure];
        if (!rankMap[measureValue]) {
            rankMap[measureValue] = {
                isTied: false,
                value: index + 1,
            };
        } else {
            rankMap[measureValue].isTied = true;
        }
    });

    return leaders.map((leader, index) => {
        const className = index === activeLeaderIndex ? 'active' : '';
        const measureValue = getLeaderValue(leader, measure, activeCategory);
        const rank = rankMap[leader[measure]];
        const rankDisplay = `${rank.isTied ? intl.formatMessage(commonMessages.labels.ties) : ''}${rank.value}.`;

        return (
            <LeaderListItem
                key={leader.player.id}
                className={className}
                onMouseOver={() => setActiveLeaderIndex(index)}
                onClick={() => setActiveLeaderIndex(index)}
            >
                <LeaderListRank>{rankDisplay}</LeaderListRank>
                <LeaderListName>{`${leader.player.firstName} ${leader.player.lastName}`}</LeaderListName>
                <LeaderListValue>{measureValue}</LeaderListValue>
            </LeaderListItem>
        );
    });
};

const LeaderList: FC<LeaderListProps> = ({
    activeCategory,
    activeLeaderIndex,
    activeMeasure,
    setActiveLeaderIndex,
    seasonId,
}) => {
    const intl = useIntl();
    if (activeMeasure.leaders.length === 0) {
        return (
            <NoDataMessage>
                <FormattedMessage {...commonMessages.labels.noQualifyingData} />
            </NoDataMessage>
        );
    }

    const activeLeader = activeMeasure.leaders[activeLeaderIndex];
    const activeMeasureLink = decodeURIComponent(activeMeasure.allLeadersLink);

    return (
        <div role="tabpanel" id={`${activeCategory}-tabpanel`} aria-labelledby={activeCategory}>
            <LeaderContent>
                <LeaderSpotlight
                    categoryLabel={activeCategory}
                    leader={activeLeader}
                    seasonId={seasonId}
                    value={getLeaderValue(activeLeader, activeMeasure.measure, activeCategory)}
                />
                <LeaderListContainer className="leaders-list">
                    {genLeaderList(activeMeasure, activeCategory, activeLeaderIndex, setActiveLeaderIndex, intl)}
                </LeaderListContainer>
            </LeaderContent>
            <LeadersNavLink to={activeMeasureLink} onClick={scrollToTop}>
                <FormattedMessage {...commonMessages.labels.allLeaders} />
            </LeadersNavLink>
        </div>
    );
};

export default React.memo(LeaderList);
